import { ReactElement, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { ConfigureFlowsheetModalController } from "@/Flowsheets/Components/ConfigureFlowsheetModal/ConfigureFlowsheetModalController";
import { EncounterDateContextProvider } from "@/Flowsheets/Contexts/EncounterDateContext";
import useFlowsheetModule from "@/Flowsheets/Hooks/useFlowsheetModule";
import { useGetFlowsheetName } from "@/Flowsheets/Hooks/useGetFlowsheetName";
import { FlowsheetSummary } from "@/Flowsheets/Models/FlowsheetSummary";
import { Flowsheet } from "../Flowsheet/Flowsheet";
import { FlowsheetHistory } from "../FlowsheetHistory/FlowsheetHistory";
import { FlowsheetWarningPopup } from "../FlowsheetWarningPopup/FlowsheetWarningPopup";

import styles from "./FlowsheetModule.module.scss";

interface IFlowsheetModuleProps {
    /**
     * The metadata of the flowsheet
     */
    readonly metadata: FlowsheetSummary;

    /**
     * The ContactFacetId of the patient the flowsheet belongs to
     */
    readonly patientId: Guid;

    /**
     * The event when leaving the flowsheet
     * @returns void
     */
    leaveFlowsheet: () => void;
}

function FlowsheetModule(props: Readonly<IFlowsheetModuleProps>): ReactElement {
    const { getText } = useTranslation();
    const { getName } = useGetFlowsheetName();

    const [isConfigureFlowsheetModalOpen, setIsConfigureFlowsheetModalOpen] = useState(false);

    const {
        flowsheet,
        flowsheetHistory,
        careComponentsData,
        setCareComponentsData,
        encounterDate,
        hasNotFoundError,
        updateFlowsheetWithTemplate,
        navigateToFlowsheetFromSummaries,
        patientContactId,
        filterSettings,
        setFilterSettings
    } = useFlowsheetModule({
        metadata: props.metadata,
        patientId: props.patientId
    });

    return (
        <>
            <div className={styles.flowsheetModuleContainer}>
                <FlowsheetHistory
                    leaveFlowsheet={props.leaveFlowsheet}
                    selectedFlowsheetId={props.metadata.Id}
                    patientId={props.patientId}
                    onFlowsheetSelect={navigateToFlowsheetFromSummaries}
                    filteredHistory={flowsheetHistory.flowsheetHistory}
                    searchTerm={flowsheetHistory.searchTerm}
                    onSearch={flowsheetHistory.setSearchTerm}
                />
                {careComponentsData && flowsheet && (
                    <EncounterDateContextProvider encounterDate={encounterDate}>
                        <Flowsheet
                            flowsheet={flowsheet}
                            title={getName(props.metadata.Template)}
                            leaveFlowsheet={props.leaveFlowsheet}
                            setCareComponentData={setCareComponentsData}
                            careComponentData={careComponentsData}
                            encounterDate={encounterDate}
                            onClickConfigureFlowsheet={() => setIsConfigureFlowsheetModalOpen(true)}
                            patientContactId={patientContactId}
                            filterSettings={filterSettings}
                            setFilterSettings={setFilterSettings}
                        />
                    </EncounterDateContextProvider>
                )}
            </div>

            {hasNotFoundError && (
                <FlowsheetWarningPopup
                    errorMessages={[getText("Flowsheets_DeletedWarningMessage")]}
                    onConfirm={props.leaveFlowsheet}
                />
            )}

            {isConfigureFlowsheetModalOpen && (
                <ConfigureFlowsheetModalController
                    currentTemplateId={props.metadata.Template.Id}
                    flowsheetId={props.metadata.Id}
                    patientId={props.patientId}
                    onApplySavedFlowsheetTemplateToCurrentFlowsheet={updateFlowsheetWithTemplate}
                    onClose={() => setIsConfigureFlowsheetModalOpen(false)}
                />
            )}
        </>
    );
}

export { FlowsheetModule };
